// ─────────────────────────────────────────────────────────────────────────────
// SERPS RBAC — Single source of truth (MOE/LTK/55/2025/2026 §2.2.15)
// ─────────────────────────────────────────────────────────────────────────────

import type { AuthUser } from './auth';

// ── Role strings (must match JWT payload exactly) ─────────────────────────────
export const ROLES = {
  SUPER_ADMIN:     'super_admin',
  MOE_ADMIN:       'moe_admin',
  SCHOOL_ADMIN:    'admin',
  PRINCIPAL:       'principal',
  TEACHER:         'teacher',
  CLERK:           'clerk',
  PARENT:          'parent',
  DPE_OFFICER:     'dpe_officer',
  MORA_OFFICER:    'mora_officer',
  SEN_COORDINATOR: 'sen_coordinator',
} as const;

export type RoleKey = keyof typeof ROLES;
export type RoleValue = typeof ROLES[RoleKey];

// ── Route permission matrix ────────────────────────────────────────────────────
// Maps each app route to the roles that may access it.
// Route keys match the path prefixes defined in main.tsx.
export const ROUTE_PERMISSIONS: Record<string, RoleValue[]> = {
  '/':                           Object.values(ROLES),  // all roles see dashboard

  // ── Parent portal (SIS 2.2.22–2.2.29) ───────────────────────────────────────
  '/parent/kehadiran':           [ROLES.PARENT, ROLES.SUPER_ADMIN],
  '/parent/akademik':            [ROLES.PARENT, ROLES.SUPER_ADMIN],
  '/parent/jadual':              [ROLES.PARENT, ROLES.SUPER_ADMIN],
  '/parent/laporan':             [ROLES.PARENT, ROLES.SUPER_ADMIN],
  '/parent/pekeliling':          [ROLES.PARENT, ROLES.SUPER_ADMIN],
  '/parent/komunikasi':          [ROLES.PARENT, ROLES.SUPER_ADMIN],
  '/parent/direktori':           [ROLES.PARENT, ROLES.SUPER_ADMIN],
  '/parent/profil':              [ROLES.PARENT, ROLES.SUPER_ADMIN],
  '/parent/pemberitahuan':       [ROLES.PARENT, ROLES.SUPER_ADMIN],

  // ── Admin / school operations ────────────────────────────────────────────────
  '/admin/applications':         [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL],
  '/admin/applications/:id':     [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL],
  '/admin/attendance':           [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.TEACHER],
  '/admin/attendance/alerts':    [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.TEACHER, ROLES.PARENT],
  '/admin/grades':               [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.TEACHER],
  '/admin/educators':            [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.CLERK],
  '/admin/educators/:id':        [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.CLERK, ROLES.TEACHER],
  '/admin/cpd':                  [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.TEACHER],
  '/admin/tpa':                  [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.TEACHER],
  '/admin/timetable':            [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.TEACHER, ROLES.PARENT],
  '/admin/timetable/:id':        [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.TEACHER, ROLES.PARENT],
  '/admin/timetable/substitute': [ROLES.SUPER_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.TEACHER],
  '/admin/library':              [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.CLERK, ROLES.TEACHER, ROLES.PARENT],
  '/admin/library/loans':        [ROLES.SUPER_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.CLERK],
  '/admin/library/reservations': [ROLES.SUPER_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.CLERK],
  '/admin/reports':              [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.TEACHER, ROLES.DPE_OFFICER, ROLES.MORA_OFFICER],
  '/admin/moe-dashboard':        [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN],
  '/admin/dpe':                  [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.DPE_OFFICER],
  '/admin/dpe/inspections':      [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.DPE_OFFICER],
  '/admin/dpe/approvals':        [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.DPE_OFFICER],
  '/admin/dpe/bulletins':        [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.DPE_OFFICER],
  '/admin/mora':                 [ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.MORA_OFFICER],
  '/admin/edubot':               Object.values(ROLES),
  '/admin/system-controls':      [ROLES.SUPER_ADMIN],
  '/admin/demo-controls':        [ROLES.SUPER_ADMIN],
};

// ── Sidebar nav capability flags ──────────────────────────────────────────────
// Used to show/hide nav items without duplicating the matrix above.
export interface NavCapabilities {
  showStudents:        boolean;  // Attendance, Grades — excludes Applications
  showApplications:    boolean;  // Enrolment Applications (admin/MOE only — NONE for teacher/parent)
  showAlerts:          boolean;  // Attendance alerts / parent notifications
  showEducators:       boolean;  // Educators list, CPD, TPA
  showTimetable:       boolean;
  showSubstitute:      boolean;
  showLibrary:         boolean;
  showLibraryAdmin:    boolean;  // Loans + Reservations (clerk/admin only)
  showReports:         boolean;
  showMoeDashboard:    boolean;
  showDpe:             boolean;
  showMora:            boolean;
  showDemoControls:    boolean;
  canEdit:             boolean;  // Show edit/create/delete buttons
  canApproveEnrolment: boolean;
  canMarkAttendance:   boolean;  // Teachers can mark, parents/MOE cannot
  isReadOnly:          boolean;  // Show 🔒 lock on pages
}

// ── Helper: get primary role string from AuthUser ─────────────────────────────
export function primaryRole(user: AuthUser): RoleValue {
  const priority: RoleValue[] = [
    ROLES.SUPER_ADMIN, ROLES.MOE_ADMIN, ROLES.DPE_OFFICER, ROLES.MORA_OFFICER,
    ROLES.SEN_COORDINATOR, ROLES.SCHOOL_ADMIN, ROLES.PRINCIPAL, ROLES.CLERK,
    ROLES.TEACHER, ROLES.PARENT,
  ];
  for (const r of priority) {
    if (user.roles.some((e) => e.role === r)) return r;
  }
  return ROLES.TEACHER;
}

export function hasRole(user: AuthUser, ...roles: RoleValue[]): boolean {
  return user.roles.some((e) => roles.includes(e.role as RoleValue));
}

export function schoolIdFromUser(user: AuthUser): string | null {
  const entry = user.roles.find((r) =>
    r.role !== ROLES.SUPER_ADMIN &&
    r.role !== ROLES.MOE_ADMIN &&
    r.schoolId !== null,
  );
  return entry?.schoolId ?? null;
}

// ── Derive nav capabilities from AuthUser ─────────────────────────────────────
export function getNavCapabilities(user: AuthUser): NavCapabilities {
  const role = primaryRole(user);
  const isSuperAdmin  = role === ROLES.SUPER_ADMIN;
  const isMoeAdmin    = role === ROLES.MOE_ADMIN;
  const isSchoolAdmin = role === ROLES.SCHOOL_ADMIN || role === ROLES.PRINCIPAL;
  const isTeacher     = role === ROLES.TEACHER;
  const isParent      = role === ROLES.PARENT;
  const isDpe         = role === ROLES.DPE_OFFICER;
  const isMora        = role === ROLES.MORA_OFFICER;

  return {
    // STUDENTS section — teachers see attendance/grades but NOT enrolment applications
    showStudents:        isSuperAdmin || isMoeAdmin || isSchoolAdmin || isTeacher || isParent,
    // Applications / enrolment — teachers and parents have NONE (tender matrix)
    showApplications:    isSuperAdmin || isMoeAdmin || isSchoolAdmin,
    // Alerts — parents can see their child's absence alerts (READ own)
    showAlerts:          isSuperAdmin || isMoeAdmin || isSchoolAdmin || isTeacher || isParent,
    // EDUCATORS section — only admin/MOE see full educator management
    showEducators:       isSuperAdmin || isMoeAdmin || isSchoolAdmin,
    // Timetable — parents see child's timetable (READ child)
    showTimetable:       isSuperAdmin || isMoeAdmin || isSchoolAdmin || isTeacher || isParent,
    // Substitute — only staff who manage classes
    showSubstitute:      isSuperAdmin || isSchoolAdmin || isTeacher,
    // Library catalogue — parents & teachers READ; admin FULL
    showLibrary:         isSuperAdmin || isMoeAdmin || isSchoolAdmin || isTeacher || isParent,
    // Library admin (loans/reservations) — clerk + admin only
    showLibraryAdmin:    isSuperAdmin || isSchoolAdmin,
    // Reports — teacher sees own class reports; DPE/MORA see their own
    showReports:         isSuperAdmin || isMoeAdmin || isSchoolAdmin || isTeacher || isDpe || isMora,
    // MOE Dashboard — national view, MOE/super only
    showMoeDashboard:    isSuperAdmin || isMoeAdmin,
    // DPE section — DPE officer, MOE (read), super
    showDpe:             isSuperAdmin || isMoeAdmin || isDpe,
    // MORA section — MORA officer, MOE (read), super
    showMora:            isSuperAdmin || isMoeAdmin || isMora,
    // Demo controls — super_admin only
    showDemoControls:    isSuperAdmin,
    // Action capabilities
    canEdit:             isSuperAdmin || isSchoolAdmin,
    canApproveEnrolment: isSuperAdmin || isSchoolAdmin,
    canMarkAttendance:   isSuperAdmin || isSchoolAdmin || isTeacher,
    // Read-only badge visibility
    isReadOnly:          isMoeAdmin || isParent || isDpe || isMora,
  };
}

// ── Route guard: can this user access this path? ──────────────────────────────
export function canAccessRoute(user: AuthUser, path: string): boolean {
  const role = primaryRole(user);
  if (role === ROLES.SUPER_ADMIN) return true;

  // Match most-specific route first
  const sortedKeys = Object.keys(ROUTE_PERMISSIONS).sort((a, b) => b.length - a.length);
  for (const pattern of sortedKeys) {
    if (pathMatchesPattern(path, pattern)) {
      const allowed = ROUTE_PERMISSIONS[pattern];
      return (allowed ?? []).includes(role);
    }
  }
  return false;
}

function pathMatchesPattern(path: string, pattern: string): boolean {
  // Turn :param segments into wildcards
  const regex = new RegExp(
    '^' + pattern.replace(/:[^/]+/g, '[^/]+') + '(/.*)?$',
  );
  return regex.test(path);
}

// ── Role display labels (BM / EN) ─────────────────────────────────────────────
export const ROLE_LABELS: Record<RoleValue, { ms: string; en: string; badge: string }> = {
  super_admin:     { ms: 'Pentadbir Super',        en: 'Super Administrator',    badge: '⚙️ Super Admin' },
  moe_admin:       { ms: 'Pegawai KPM',            en: 'MOE Officer',            badge: '🏛️ KPM' },
  admin:           { ms: 'Pentadbir Sekolah',      en: 'School Admin',           badge: '🏫 Admin' },
  principal:       { ms: 'Pengetua',               en: 'Principal',              badge: '🏫 Pengetua' },
  teacher:         { ms: 'Guru',                   en: 'Teacher',                badge: '📚 Guru' },
  clerk:           { ms: 'Kerani',                 en: 'Clerk',                  badge: '📋 Kerani' },
  parent:          { ms: 'Ibu Bapa / Penjaga',     en: 'Parent / Guardian',      badge: '👪 Ibu Bapa' },
  dpe_officer:     { ms: 'Pegawai DPE',            en: 'DPE Officer',            badge: '🏢 DPE' },
  mora_officer:    { ms: 'Pegawai MORA',           en: 'MORA Officer',           badge: '☪️ MORA' },
  sen_coordinator: { ms: 'Koordinator SEN',        en: 'SEN Coordinator',        badge: '♿ SEN' },
};
